<template>
  <div class="musubi-page musubi-page--scout">
    <header class="musubi-page__head">
      <h1>AI Scout</h1>
      <p class="text-muted">
        Give the Scout a target profile + seed sites; it evaluates each for fit
        (AI score + rationale) and proposes a batch. Approve a candidate to admit
        it to the cold pipeline (enriched automatically) — the Scout never contacts anyone.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <div class="scout-grid">
      <!-- New run + runs list -->
      <div class="scout-left">
        <MusubiSectionCard title="New scout run" icon="bolt">
          <label class="form-label small text-muted">Target profile</label>
          <textarea v-model="form.profile" rows="3" class="form-control form-control-sm mb-2"
            placeholder="e.g. UK independent retailers on WordPress that would benefit from a rebuild"></textarea>
          <label class="form-label small text-muted">Seed URLs (one per line)</label>
          <textarea v-model="form.seeds" rows="4" class="form-control form-control-sm mb-2"
            placeholder="https://example-agency.co.uk&#10;https://another-firm.com"></textarea>
          <button class="btn btn-primary btn-sm" :disabled="busy || !canRun" @click="createRun">
            <CygnusIcon name="spinner" :spin="true" v-if="busy" /><span v-else>Queue run</span>
          </button>
          <div class="form-text small">Runs execute in the background (cron). Refresh to see results.</div>
        </MusubiSectionCard>

        <MusubiSectionCard title="Runs" icon="stream">
          <template #actions>
            <button class="btn btn-sm btn-link" :disabled="loading" @click="loadRuns"><CygnusIcon name="refresh" /></button>
          </template>
          <div v-if="!runs.length" class="text-muted small py-2">No runs yet.</div>
          <ul v-else class="scout-runs">
            <li v-for="r in runs" :key="r.id" :class="{ 'is-active': selectedId === r.id }" @click="openRun(r.id)">
              <span class="scout-run__id">#{{ r.id }}</span>
              <span class="scout-run__profile">{{ r.target_profile }}</span>
              <MusubiStatusPill :status="r.status" />
              <span class="scout-run__count">{{ r.candidate_count }}</span>
            </li>
          </ul>
        </MusubiSectionCard>
      </div>

      <!-- Selected run candidates -->
      <MusubiSectionCard :title="detail.run ? `Run #${detail.run.id} — candidates` : 'Candidates'" icon="users">
        <div v-if="!detail.run" class="text-muted small py-3">Select a run to review its candidates.</div>
        <template v-else>
          <p class="text-muted small">{{ detail.run.target_profile }}</p>
          <div v-if="detail.run.status !== 'complete'" class="alert alert-info py-2 small">
            Run is <strong>{{ detail.run.status }}</strong>{{ detail.run.error ? ': ' + detail.run.error : ' — refresh shortly.' }}
          </div>
          <table v-if="detail.candidates.length" class="scout-table">
            <thead><tr><th>Fit</th><th>Rec</th><th>Site</th><th>Rationale</th><th></th></tr></thead>
            <tbody>
              <tr v-for="c in detail.candidates" :key="c.id">
                <td><span class="scout-fit" :class="recClass(c.recommendation)">{{ c.fit_score }}</span></td>
                <td><MusubiStatusPill :status="c.recommendation || 'maybe'" /></td>
                <td><a :href="c.url" target="_blank" rel="noopener">{{ c.host }}</a><div class="scout-nm">{{ c.name }}</div></td>
                <td class="scout-why">{{ c.rationale }}</td>
                <td class="scout-acts">
                  <template v-if="c.status === 'proposed'">
                    <button class="btn btn-sm btn-link p-0 me-2" :disabled="busy" @click="approve(c.id)">Approve</button>
                    <button class="btn btn-sm btn-link p-0 text-danger" :disabled="busy" @click="reject(c.id)">Reject</button>
                  </template>
                  <span v-else class="text-muted small">{{ c.status }}<template v-if="c.discovered_lead_id"> → #{{ c.discovered_lead_id }}</template></span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-muted small py-2">No candidates.</p>
        </template>
      </MusubiSectionCard>
    </div>
  </div>
</template>

<script>
/**
 * AdminScout — Part 1 §5 review surface for the AI Scout. Queue a run (target
 * profile + seed URLs), watch runs execute (cron, async), and review each run's
 * scored candidate batch — approve (→ cold DiscoveredLead) or reject. Composes
 * the design-system primitives over /api/v1/admin/scout/*.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminScout',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      runs: [],
      detail: { run: null, candidates: [] },
      selectedId: null,
      form: { profile: '', seeds: '' },
      loading: false,
      busy: false,
      error: null,
      message: '',
    };
  },
  computed: {
    canRun() { return this.form.profile.trim() && this.seedList().length; },
  },
  mounted() { this.loadRuns(); },
  methods: {
    seedList() {
      return this.form.seeds.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    },
    async loadRuns() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/scout/runs');
        this.runs = this.unwrap(res);
        if (this.selectedId) await this.openRun(this.selectedId);
      } catch (e) { this.error = e?.message || 'Failed to load runs.'; }
      this.loading = false;
    },
    async createRun() {
      this.busy = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post('/api/v1/admin/scout/runs', {
          target_profile: this.form.profile.trim(),
          seeds: this.seedList(),
        });
        const p = res?.data ?? res;
        this.message = `Queued run #${p.run_id}. It'll run in the background.`;
        this.form.seeds = '';
        await this.loadRuns();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Failed to queue run.'; }
      this.busy = false;
    },
    async openRun(id) {
      this.selectedId = id; this.error = null;
      try {
        const res = await this.api.get(`/api/v1/admin/scout/runs/${id}`);
        const p = res?.data ?? res;
        this.detail = { run: p.run || null, candidates: p.candidates || [] };
      } catch (e) { this.error = e?.message || 'Failed to load run.'; }
    },
    async approve(id) { await this.act(`/api/v1/admin/scout/candidates/${id}/approve`, 'Approved'); },
    async reject(id)  { await this.act(`/api/v1/admin/scout/candidates/${id}/reject`, 'Rejected'); },
    async act(url, label) {
      this.busy = true; this.error = null; this.message = '';
      try {
        await this.api.post(url, {});
        this.message = `${label}.`;
        await this.openRun(this.selectedId);
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Action failed.'; }
      this.busy = false;
    },
    recClass(rec) { return rec === 'pursue' ? 'is-pursue' : (rec === 'skip' ? 'is-skip' : 'is-maybe'); },
    unwrap(res) {
      const p = res?.data?.data ?? res?.data ?? res;
      return Array.isArray(p) ? p : (p?.rows || []);
    },
  },
};
</script>

<style scoped>
.musubi-page--scout { padding: var(--m-space-6, 32px); }
.scout-grid { display: grid; grid-template-columns: 360px 1fr; gap: 16px; align-items: start; }
.scout-left { display: grid; gap: 16px; }
.scout-runs { list-style: none; margin: 0; padding: 0; }
.scout-runs li { display: flex; align-items: center; gap: 8px; padding: 8px 6px; border-bottom: 1px solid var(--m-border); cursor: pointer; font-size: 0.82rem; }
.scout-runs li:hover { background: var(--m-bg-content); }
.scout-runs li.is-active { background: var(--m-accent-soft); }
.scout-run__id { color: var(--m-text-tertiary); font-weight: 700; }
.scout-run__profile { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.scout-run__count { color: var(--m-text-tertiary); font-weight: 700; }
.scout-table { width: 100%; border-collapse: collapse; }
.scout-table th { text-align: left; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 8px 6px; border-bottom: 1px solid var(--m-border); }
.scout-table td { padding: 10px 6px; border-bottom: 1px solid var(--m-border); vertical-align: top; font-size: 0.85rem; }
.scout-fit { display: inline-block; min-width: 30px; text-align: center; font-weight: 800; border-radius: 6px; padding: 2px 6px; }
.scout-fit.is-pursue { background: var(--m-status-success, #16a34a); color: #fff; }
.scout-fit.is-maybe { background: var(--m-accent-soft); color: var(--m-accent); }
.scout-fit.is-skip { background: var(--m-bg-content); color: var(--m-text-tertiary); }
.scout-nm { color: var(--m-text-tertiary); font-size: 0.72rem; }
.scout-why { color: var(--m-text-secondary); max-width: 420px; }
.scout-acts { white-space: nowrap; }
@media (max-width: 1024px) { .scout-grid { grid-template-columns: 1fr; } }
</style>
